import type { FC } from 'react';
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import axios from 'src/utils/axios';

/**
 * An HTML artifact, rendered inert (PA-11).
 *
 * Two independent protections, and both matter:
 *
 * 1. Thor serves the markup with `Content-Security-Policy: sandbox; default-src 'none'`,
 *    so even if the document were navigated to directly it could not run a script.
 * 2. This frame is `<iframe sandbox srcDoc>` — no `allow-scripts`, no
 *    `allow-same-origin`. `srcDoc` means the browser never fetches the artifact; the
 *    markup is written into a document with an opaque origin, so a `<script>` inside a
 *    report cell is text, not code.
 *
 * `sandbox` with an empty value is deliberate: adding `allow-scripts` *and*
 * `allow-same-origin` together would let the document remove its own sandbox, which is
 * the classic way this protection gets defeated.
 */
interface HtmlPreviewFrameProps {
  documentId: number;
  title?: string;
  height?: number | string;
}

const HtmlPreviewFrame: FC<HtmlPreviewFrameProps> = ({
  documentId,
  title = 'Report preview',
  height = '60vh',
}) => {
  const [markup, setMarkup] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        // POST, not GET: nothing ever navigates to the artifact URL.
        const response = await axios.post<string>(
          `/support/documents/${documentId}/preview/`,
          {},
          { responseType: 'text', transformResponse: [(data) => data] }
        );
        if (!cancelled) {
          setMarkup(
            typeof response.data === 'string'
              ? response.data
              : String(response.data)
          );
        }
      } catch {
        if (!cancelled) {
          setError('This report could not be opened. Download it instead.');
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (markup === null) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  return (
    <Box
      component="iframe"
      title={title}
      sandbox=""
      srcDoc={markup}
      data-testid="html-preview-frame"
      sx={{ width: '100%', height, border: 0, bgcolor: 'background.paper' }}
    />
  );
};

export default HtmlPreviewFrame;
